import React, { useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiPlus, mdiContentSave } from '@mdi/js';

const formatBalance = (value, currency) =>
  value.toLocaleString(undefined, {
    style: 'currency',
    currency: currency || 'USD'
  });

/**
 * Vertical list of saved account views (replaces the old horizontal tab bar).
 * Each item shows the view's account name plus its account balance (fetched
 * once at page level and passed in); the reconciled total appears in
 * parentheses only when it differs from the balance. Items can be closed
 * individually. The footer holds the "Save views" and "+ Add view" actions.
 */
const AccountsSidebar = ({ tabs, activeTabId, accounts, accountsBalance, onSelect, onClose, onAdd, onSaveTabs }) => {
  const itemRefs = useRef({});

  // Keep the active view visible: whenever the selection changes (e.g. adding
  // a new view or restoring saved tabs), scroll it into the list's visible
  // area. `nearest` makes this a no-op when the item is already visible and
  // only scrolls the sidebar, never the whole page.
  useEffect(() => {
    const el = itemRefs.current[activeTabId];
    if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTabId, tabs.length]);

  return (
    <aside className="accounts-sidebar">
      <div className="accounts-list">
        {tabs.map(tab => {
          const accountId = tab.apiParams.account_id ? parseInt(tab.apiParams.account_id, 10) : null;
          const account = accountId === null ? null : accounts.find(acc => acc.id === accountId);
          const balance = accountId === null ? null : accountsBalance.find(acc => acc.id === accountId);
          const label = account?.name || balance?.name || 'All accounts';

          return (
            <button
              key={tab.id}
              ref={(el) => { itemRefs.current[tab.id] = el; }}
              className={`account-item ${tab.id === activeTabId ? 'active' : ''}`}
              onClick={() => onSelect(tab.id)}
            >
              <span className="account-item-label">{label}</span>
              {balance && (
                <span className="account-item-balances">
                  {balance.reconciled_balance === balance.balance
                    ? formatBalance(balance.balance, balance.currency)
                    : `${formatBalance(balance.balance, balance.currency)} (${formatBalance(balance.reconciled_balance, balance.currency)})`}
                </span>
              )}
              <span
                className="close-view"
                onClick={(e) => onClose(tab.id, e)}
                title="Close view"
              >
                &times;
              </span>
            </button>
          );
        })}
        {tabs.length === 0 && (
          <span className="accounts-empty">No views yet</span>
        )}
      </div>

      <div className="accounts-footer">
        <button className="save-tabs-button" onClick={onSaveTabs} title="Save current view configuration">
          <Icon path={mdiContentSave} size={0.8} />
          <span>Save views</span>
        </button>
        <button className="add-view-button" onClick={onAdd} title="Add account view">
          <Icon path={mdiPlus} size={0.8} />
          <span>Add view</span>
        </button>
      </div>
    </aside>
  );
};

export default AccountsSidebar;